import { ReactNode } from "react";
import { Skeleton } from "@mui/material";
import { SettingsCardField } from "../../common";
import { CoreCopyOnlyTextField, CoreInfoIndicator, CoreRequiredIcon, CoreSkeletonWrapper } from "../../core";
import styles from "./InstitutionBaseRow.module.css";

export type InstitutionMode = "view" | "edit";

type EditMode = {
	mode: Extract<InstitutionMode, "edit">;
};

type ViewMode = {
	mode: Extract<InstitutionMode, "view">;
};

type EditStrategy = EditMode & {
	createControl: (id: string) => ReactNode;
	required?: boolean;
};

type ViewStrategy = ViewMode & {
	value: string;
	copyAriaLabel?: string;
	placeholder?: string;
};

type InstitutionInformationRowStrategy = ViewStrategy | EditStrategy;

type InstitutionInformationRowProps = {
	label: ReactNode;
	description: ReactNode;
	hint?: ReactNode;
	/**@default false */
	loading?: boolean;
	strategy: InstitutionInformationRowStrategy;
};

export default function InstitutionBaseRow({
	strategy,
	label,
	description,
	hint,
	loading = false,
}: InstitutionInformationRowProps) {
	const required = strategy.mode === "edit" && strategy.required;

	return (
		<SettingsCardField
			custom={{ orientation: "horizontal" }}
			renderers={{
				label: {
					titleRenderer: () => {
						return (
							<CoreSkeletonWrapper loading={loading} variant="text" height={40} width="min(65%, 280px)">
								<span className={styles.title}>
									{label}
									{required && <CoreRequiredIcon />}
									{hint && (
										<span
											className={styles.hint}
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
											}}
										>
											<CoreInfoIndicator hint={hint} size={20} />
										</span>
									)}
								</span>
							</CoreSkeletonWrapper>
						);
					},

					subTitleRenderer: () => (
						<CoreSkeletonWrapper loading={loading} variant="text" height={30} width="min(100%, 520px)">
							{description}
						</CoreSkeletonWrapper>
					),
				},
			}}
			slots={{
				contentField: {
					className: styles.institutionBaseRowContentField,
				},
			}}
		>
			{(id) => {
				if (loading) return <Skeleton variant="rounded" width={"100%"} height={70} aria-hidden="true" />;
				if (strategy.mode === "view") {
					return (
						<CoreCopyOnlyTextField
							ariaLabelledBy={id}
							value={strategy.value ?? ""}
							copyButtonAriaLabel={strategy.copyAriaLabel}
							placeholder={strategy.placeholder}
						/>
					);
				}
				return strategy.createControl(id);
			}}
		</SettingsCardField>
	);
}

export type CommonConsumerProps = Pick<InstitutionInformationRowProps, "loading">;

export type ViewConsumerProps = CommonConsumerProps & ViewMode;
export type EditConsumerProps = CommonConsumerProps &
	EditMode & {
		disabled?: boolean;
		disabledTooltip?: string;
		saving?: boolean;
	};

// Internal use
export type HeaderRowProps = CommonConsumerProps &
	Pick<InstitutionInformationRowProps, "label" | "description" | "hint">;
